import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'
import { LINKS } from './links'

/**
 * The DAIMI Certification Course — the page consultants land on from
 * "Get Certified as an Expert".
 *
 * It was an **imported** WordPress page until today
 * (`content/imported/pages/en/digital-ai-maturity-index-daimi-course.json`,
 * still on disk as the record of what digitopia.co says). The scrape kept the
 * words and lost the page: fifteen paragraphs in a row, the curriculum's four
 * modules reduced to their bodies with the module names gone, the price and the
 * enrolment ask reading as two more paragraphs, and no picture anywhere.
 *
 * Asked for as *"Daimi course sayfa tesarımı ve görselleri"* with four
 * screenshots of the live page, so it is a written page now, band for band the
 * reference's and in this site's own section vocabulary: the centred hero with
 * one Register button, the "Lead the Next Era of Consulting" card with the
 * Learning Platform tablet beside it, "Why DAIMI Certification?" and its four
 * reasons over the photograph of a consultant reading the platform, the Course
 * Curriculum, "What is DAIMI?" beside the framework wheel, and the tuition ask.
 *
 * **The copy is digitopia.co's own, verbatim**, taken from the imported JSON
 * rather than transcribed off a screenshot — the only sentences written here are
 * the four curriculum module names, which the scrape dropped and the screenshots
 * show (see the check on that section).
 *
 * Where it departs from the reference, and why:
 *
 *  * **The tuition band ends on buttons, not a nine-field form.** The reference
 *    closes on a HubSpot form; no form on this site submits anywhere yet, and
 *    every document already carries the closing contact band
 *    (`components/ContactBand.tsx`) at its foot. Same decision, and the same
 *    reasoning, as the DAIMI solution page and the e-book pages.
 *  * **The curriculum is a list, not an accordion.** The reference's four
 *    modules open on click; the site has no accordion section and the bodies are
 *    two lines each, so they are simply on the page.
 */
export const route: RouteDef = {
  id: 'daimiCourse',
  path: 'digital-ai-maturity-index-daimi-course',
  priority: 0.8,
}

export const daimiCourse: WrittenPage = {
  navLabel: 'DAIMI Certification Course',
  title: 'Digital & AI Maturity Index Certification Course',
  description:
    'Become a Certified DAIMI Practitioner. Gain practical knowledge in digital and AI transformation and position yourself as a trusted advisor with this comprehensive online training.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Digital & AI Maturity Index (DAIMI)',
      headline: 'Digital & AI Maturity Index Certification Course',
      sub: 'Become a Certified DAIMI Practitioner',
      body: [
        'Gain practical knowledge in digital and AI transformation and position yourself as a trusted advisor with this comprehensive online training.',
      ],
      ctas: [{ label: 'Register Now', href: '#contact' }],
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'Lead the Next Era of Consulting',
      body: [
        'In an era where Digital and AI are no longer separate initiatives, businesses need a unified roadmap. The Digital & AI Maturity Index (DAIMI) is the industry-leading framework designed to measure, benchmark, and accelerate this twin transformation.',
        'By becoming a Certified DAIMI Consultant, you gain the framework, tools, and credibility to guide organizations through complex digital evolutions with data-driven precision.',
      ],
      photo: {
        src: '/photo/learning-platform-tablet.webp',
        alt: 'The Digitopia Learning Platform sign-in screen on a tablet.',
      },
      ctas: [{ label: 'Register Now', href: '#contact' }],
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Why DAIMI Certification?',
      points: [
        'Proven Framework: Access the comprehensive framework developed by Digitopia to conduct rigorous, evidence-based assessments.',
        'Competitive Edge: Stand out as a transformation leader capable of aligning C-suite executives around a common language.',
        'Exclusive Tools: Gain access to the Impact Platform and benchmarking data to compare performance against industry peers.',
        'Be Part of the Ecosystem: Join the Digitopia Connect Platform network after certification to connect with like-minded professionals and companies seeking your specialized skills in digital maturity.',
      ],
      photo: {
        src: '/photo/consultant-impact-platform-recommendations.webp',
        alt: 'A consultant reading the Impact Platform’s recommendations and bubble chart on a desktop screen.',
      },
      ctas: [{ label: 'Register', href: '#contact' }],
    },
    {
      kind: 'features',
      heading: 'Course Curriculum',
      sub: 'The certification program is structured to take you from foundational theory to expert practical application.',
      columns: 2,
      textSize: 'lg',
      items: [
        {
          title: 'Introduction to Digital Transformation & AI',
          body: 'Understand the “Twin Transformation” reality. Learn why digital foundations are essential for AI scalability and how AI accelerates digital disruption.',
        },
        {
          title: 'The DAIMI Framework',
          body: 'Deep dive into the Six Dimensions (Customer, Operations, Innovation, People, Governance, Technology) and the Five Maturity Levels (from Initial to Optimizing).',
        },
        {
          title: 'The New Consulting Framework',
          body: 'Master the practitioner’s journey: Assess — conduct effective stakeholder interviews and gather evidence. Benchmark — calibrate maturity levels against global industry standards. Prioritize — identify high-impact improvement areas and “quick wins.”',
        },
        {
          title: 'The Impact Platform',
          body: 'Learn to use our proprietary platform to visualize findings through “Snowflake Analysis,” track “Vitality Scores,” and build actionable transformation roadmaps.',
        },
      ],
      footnote:
        'The DAIMI Certification Course is launching soon. We are currently accepting interest from consultants, digital leaders, and transformation practitioners who want to be part of the first cohort.',
      cta: { label: 'Enroll Now', href: '#contact' },
      check:
        'The four module names above are read off the screenshots of '
        + 'digitopia.co/digital-ai-maturity-index-course, not off the scrape — the '
        + 'WordPress import kept each module’s body and dropped the heading it sat '
        + 'under, so the titles are transcribed from a picture. The bodies are '
        + 'verbatim. Confirm the four names, and tell me the launch date if the '
        + '“launching soon” line above should now say one.',
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'What is DAIMI?',
      body: [
        'The Digital and AI Maturity Index (DAIMI) helps organizations understand how effectively digital and AI capabilities are defined, governed, and executed across the enterprise. It identifies maturity gaps and structural constraints, benchmarks performance against peers, and guides focused improvement priorities. DAIMI supports turning strategy into execution through a shared baseline, clear roadmap, and continuous progress visibility.',
      ],
      /*
       * The labelled export, not the white-lettered one. This card is a
       * `bg-band` plate — a light grey surface — so the version that names the
       * six dimensions and their sub-items around the wheel is the one that
       * reads here. The white-lettered file is for the dark methodology band
       * (`content/reportClosing.ts`), where these navy labels would disappear.
       */
      photo: {
        src: '/photo/daimi-framework-wheel-labelled.png',
        alt: 'The DAIMI wheel: the six dimensions — People, Customer, Operations, Innovation, Governance and Technology — around the Digital & AI Maturity Index.',
      },
      ctas: [
        { label: 'Learn more', href: LINKS.daimi },
        { label: 'Talk to an expert', href: '#contact' },
      ],
    },
    {
      kind: 'prose',
      align: 'center',
      size: 'lg',
      heading: 'Scale AI with Confidence: Register for DAIMI Course',
      sub: 'The standard tuition for the DAIMI Practitioner Course is $450 USD. We offer exclusive pricing and promotional rates.',
      paragraphs: [
        'To inquire about your eligibility for a discount or to secure your spot in our upcoming cohort, please fill out the form below. Our team will reach out with your personalized enrollment details.',
      ],
    },
  ],
}
